import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBoolean, useUpdateEffect } from 'react-use'
import { getNotificationValues } from '@/services/NotificationServices'
import { toggleChecks } from '@/utils/acl'

import { NotificationType } from '@makyoapp/makyo-sdk/models/User'
import { NotificationSettingsAdapter } from '@makyoapp/makyo-common'
import { Notif24, Sms16 } from '@makyoapp/makyo-icon/icon'
import { CheckboxTableKey, CheckboxTableValue } from '@makyoapp/makyo-ui'

import { useUpdateAclMutation } from '@/resolvers/user/mutation'
import useAuth from '@/hooks/useAuth'
import { useReqError } from '@/hooks/useReqError'

/**
 * `useMyNotificationSettings` is a custom hook that provides access to the user's notification settings.
 *
 * It manages state for notification settings values, as well as the state for whether changes are being saved, and whether they have been saved.
 *
 * It also provides a function `handleClick` to handle user interactions with the checkboxes representing the notification settings.
 *
 * This hook is designed to be used in components where the user can view and modify their notification settings.
 *
 * @returns {Object} An object containing the following:
 * - `values`: The current notification settings values
 * - `isSaving`: A boolean indicating whether changes are currently being saved
 * - `isSaved`: A boolean indicating whether changes have been successfully saved
 * - `handleClick`: A function to handle checkbox click events
 */
const useMyNotificationSettings = () => {
  const { t } = useTranslation()
  const { userData, updateNotifications } = useAuth()
  const printAPIError = useReqError()

  const { mutate: updateAclMutation } = useUpdateAclMutation()

  const [isSaved, setSaved] = useBoolean(false)
  const [isSaving, setIsSaving] = useBoolean(false)
  const [changing, setChanging] = useBoolean(false)
  const [checkAllData, setCheckAllData] = useState<Record<string, string[]>>({})

  const [values, setValues] = useState<NotificationSettingsAdapter[] | undefined>(
    userData?.notifications ? getNotificationValues(userData?.notifications) : undefined
  )

  // Array that defines the keys for the Checkbox table. Each key represents a notification type (Email or In-app).
  const keys: CheckboxTableKey<NotificationType>[] = [
    {
      id: NotificationType.Email,
      label: t('Email'),
      icon: <Sms16 />,
    },
    {
      id: NotificationType.Push,
      label: t('In-App'),
      icon: <Notif24 width={16} height={16} />,
    },
  ]

  // Callback function that sets the `isSaved` state. If `newSaved` is true, it sets `isSaved` to true and after 3 seconds sets it back to false.
  const setIsSaved = useCallback(
    (newSaved: boolean) => {
      if (newSaved) {
        setSaved(newSaved)
        return new Promise(() => setTimeout(() => setSaved(false), 3000))
      }
    },
    [setSaved]
  )

  // State to hold the next values
  const [nextValues, setNextValues] = useState<NotificationSettingsAdapter[]>([])

  // State to hold the timer id
  const [timerId, setTimerId] = useState<NodeJS.Timeout | null>(null)

  // A useEffect hook to listen for changes in nextValues
  useUpdateEffect(() => {
    // If timerId exists, clear the previous timeout
    if (timerId) {
      clearTimeout(timerId)
    }

    // Set a timeout to execute the mutation after a delay of 1 second
    const id = setTimeout(() => {
      // Set isSaving to true before starting the mutation
      setIsSaving(true)

      // Call the mutation function
      updateAclMutation(
        { nextValues },
        {
          onError(e) {
            // Handle any errors
            printAPIError(e)
          },
          onSettled() {
            // Once the mutation is completed, set isSaving to false and isSaved to true
            setIsSaving(false)
            setIsSaved(true)
          },
        }
      )
    }, 2000) // Delay of 1000ms

    // Save the id of the timeout in the state
    setTimerId(id)
  }, [nextValues]) // This effect hook depends on nextValues. It will be called every time nextValues change.

  // Function to update nextValues. It will trigger the useEffect hook.
  const toggleValues = (newValues: NotificationSettingsAdapter[]) => {
    setNextValues(newValues)
  }

  // The handleClick function handles the click events on the checkboxes within the table.
  // It accepts three parameters:
  // 1. scopeIdx - The index of the scope (or row) within which the click event occurred.
  // 2. check - The current state (checked or unchecked) of the checkbox that was clicked.
  // 3. key - The corresponding key (or ID) of the checkbox that was clicked.
  const handleClick = (scopeIdx: number, check: string, key: CheckboxTableKey<NotificationType>) => {
    setSaved(false) // First, it resets the 'isSaved' state to false.
    setIsSaving(false) // It also resets the 'isSaving' state to false.

    // If there are any values present,
    if (values) {
      // Call the 'toggleChecks' function to update the checked state of the checkboxes.
      // This function takes in the scope index, current check state, the key id, current values, and the 'toggleValues' function as parameters.
      toggleChecks(scopeIdx, check, key.id, values, toggleValues)
    }
  }

  const handleCheckAll = (scopeIdx: number, key: CheckboxTableKey<NotificationType>, isChecked: boolean) => {
    setSaved(false) // First, it resets the 'isSaved' state to false.
    setIsSaving(false) // It also resets the 'isSaving' state to false.

    if (values) {
      let getList = values[scopeIdx].checks
      let cpValues = [...values] as CheckboxTableValue<string>[]

      Object.entries(getList).map((val: [string, string[]]) => {
        if (isChecked) {
          cpValues[scopeIdx].checks[val[0]] = [...cpValues[scopeIdx].checks[val[0]], key.id]
        } else {
          cpValues[scopeIdx].checks[val[0]] = cpValues[scopeIdx].checks[val[0]].filter(
            (item: string) => item !== key.id
          )
        }
      })

      if (isChecked) {
        setCheckAllData((prevState) => {
          const newState = { ...prevState }
          if (scopeIdx in newState) {
            newState[scopeIdx].push(key.id)
          } else {
            newState[scopeIdx] = [key.id]
          }
          return newState
        })
      } else {
        setCheckAllData((prevState) => {
          const newState = { ...prevState }
          if (scopeIdx in newState) {
            const index = newState[scopeIdx].indexOf(key.id)
            if (index > -1) {
              newState[scopeIdx].splice(index, 1)
            }
            if (newState[scopeIdx].length === 0) {
              delete newState[scopeIdx]
            }
          }
          return newState
        })
      }

      setNextValues(cpValues)
    }
  }

  return {
    isSaved,
    isSaving,
    values,
    keys,
    handleClick,
    handleCheckAll,
    checkAllData,
    t,
  }
}

export default useMyNotificationSettings
